import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Offcanvas,
  OffcanvasBody,
  OffcanvasHeader,
  Pagination,
  PaginationItem,
  PaginationLink,
  Row,
  Spinner,
  Table,
} from "reactstrap";

import BreadCrumb from "../../../../Components/Common/BreadCrumb";
import EnrichmentCard from "../../../../Components/SuperAdmin/EnrichmentCard";
import {
  getEnrichment,
  getTrialQueue,
  TenantEnrichment,
  TrialQueueItem,
} from "../../../../services/adminCsService";
import { PaginationMeta } from "../../../../types/api";

const PER_PAGE = 15;

const formatDateTime = (isoDate: string | null): string => {
  if (!isoDate) {
    return "—";
  }

  const date = new Date(isoDate);

  if (Number.isNaN(date.getTime())) {
    return isoDate;
  }

  return date.toLocaleString(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const resolveScoreColor = (score: number): string => {
  if (score >= 75) {
    return "success";
  }

  if (score >= 45) {
    return "warning";
  }

  return "secondary";
};

const SuperAdminTrialQueue = () => {
  const { t } = useTranslation();

  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState<TrialQueueItem[]>([]);
  const [meta, setMeta] = useState<PaginationMeta | null>(null);
  const [page, setPage] = useState(1);

  const [selectedTenant, setSelectedTenant] = useState<TrialQueueItem | null>(null);
  const [enrichment, setEnrichment] = useState<TenantEnrichment | null>(null);
  const [enrichmentLoading, setEnrichmentLoading] = useState(false);

  const loadQueue = useCallback(async () => {
    setLoading(true);

    try {
      const response = await getTrialQueue({ page, per_page: PER_PAGE });

      if (response.success) {
        setItems(response.data);
        setMeta(response.meta);
      }
    } catch (error: any) {
      toast.error(error?.data || t("admin.cs.trialQueue.toast.loadError"));
    } finally {
      setLoading(false);
    }
  }, [page, t]);

  useEffect(() => {
    document.title = t("admin.cs.trialQueue.pageTitle");
  }, [t]);

  useEffect(() => {
    loadQueue();
  }, [loadQueue]);

  const openEnrichment = async (tenant: TrialQueueItem) => {
    setSelectedTenant(tenant);
    setEnrichment(null);
    setEnrichmentLoading(true);

    try {
      const response = await getEnrichment(tenant.id);

      if (response.success) {
        setEnrichment(response.data);
      }
    } catch (error: any) {
      toast.error(error?.data || t("admin.cs.enrichment.toast.loadError"));
    } finally {
      setEnrichmentLoading(false);
    }
  };

  const closeEnrichment = () => {
    setSelectedTenant(null);
    setEnrichment(null);
  };

  const paginationRange = useMemo(() => {
    if (!meta) {
      return [];
    }

    return Array.from({ length: meta.last_page }, (_, index) => index + 1);
  }, [meta]);

  return (
    <React.Fragment>
      <ToastContainer closeButton pauseOnHover />
      <div className="page-content">
        <Container fluid>
          <BreadCrumb
            title={t("admin.cs.trialQueue.breadcrumb.title")}
            pageTitle={t("admin.cs.trialQueue.breadcrumb.pageTitle")}
          />

          <Row>
            <Col lg={12}>
              <Card>
                <CardHeader>
                  <h5 className="card-title mb-0">{t("admin.cs.trialQueue.card.title")}</h5>
                  <p className="text-muted mb-0 mt-1 small">
                    {t("admin.cs.trialQueue.card.subtitle")}
                  </p>
                </CardHeader>
                <CardBody>
                  {loading ? (
                    <div className="text-center py-5">
                      <Spinner color="primary" />
                    </div>
                  ) : items.length === 0 ? (
                    <p className="text-muted mb-0">{t("admin.cs.trialQueue.empty")}</p>
                  ) : (
                    <>
                      <div className="table-responsive">
                        <Table className="align-middle mb-0 table-hover">
                          <thead className="table-light">
                            <tr>
                              <th>{t("admin.cs.trialQueue.table.tenant")}</th>
                              <th>{t("admin.cs.trialQueue.table.score")}</th>
                              <th>{t("admin.cs.trialQueue.table.trialDate")}</th>
                              <th>{t("admin.cs.trialQueue.table.ahaStatus")}</th>
                              <th>{t("admin.cs.trialQueue.table.action")}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {items.map((item) => (
                              <tr
                                key={item.id}
                                role="button"
                                onClick={() => openEnrichment(item)}
                                style={{ cursor: "pointer" }}
                              >
                                <td>
                                  <div className="fw-medium">{item.name}</div>
                                  <div className="text-muted small">
                                    {item.owner_user?.email ?? item.document}
                                  </div>
                                  <div className="d-flex flex-wrap gap-1 mt-1">
                                    {item.badges.map((badge) => (
                                      <Badge
                                        key={badge}
                                        color={badge === "at_risk_no_aha" ? "danger" : "info"}
                                        className={
                                          badge === "at_risk_no_aha"
                                            ? ""
                                            : "bg-info-subtle text-info"
                                        }
                                      >
                                        {t(`admin.cs.trialQueue.badges.${badge}`, {
                                          defaultValue: badge,
                                        })}
                                      </Badge>
                                    ))}
                                  </div>
                                </td>
                                <td>
                                  <Badge color={resolveScoreColor(item.lead_score)}>
                                    {item.lead_score}
                                  </Badge>
                                </td>
                                <td>{formatDateTime(item.trial_started_at)}</td>
                                <td>
                                  <Badge color={item.aha_completed ? "success" : "warning"}>
                                    {item.aha_completed
                                      ? t("admin.cs.trialQueue.aha.completed")
                                      : t("admin.cs.trialQueue.aha.pending")}
                                  </Badge>
                                </td>
                                <td>
                                  <Button
                                    size="sm"
                                    color="soft-primary"
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      openEnrichment(item);
                                    }}
                                  >
                                    {t("admin.cs.trialQueue.actions.viewEnrichment")}
                                  </Button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>

                      {meta && meta.last_page > 1 ? (
                        <div className="d-flex justify-content-between align-items-center mt-3">
                          <span className="text-muted small">
                            {t("admin.cs.trialQueue.pagination.showing", {
                              from: meta.from ?? 0,
                              to: meta.to ?? 0,
                              total: meta.total,
                            })}
                          </span>
                          <Pagination className="mb-0">
                            <PaginationItem disabled={page <= 1}>
                              <PaginationLink
                                previous
                                onClick={() => setPage((current) => Math.max(1, current - 1))}
                              />
                            </PaginationItem>
                            {paginationRange.map((pageNumber) => (
                              <PaginationItem key={pageNumber} active={pageNumber === page}>
                                <PaginationLink onClick={() => setPage(pageNumber)}>
                                  {pageNumber}
                                </PaginationLink>
                              </PaginationItem>
                            ))}
                            <PaginationItem disabled={page >= meta.last_page}>
                              <PaginationLink
                                next
                                onClick={() =>
                                  setPage((current) => Math.min(meta.last_page, current + 1))
                                }
                              />
                            </PaginationItem>
                          </Pagination>
                        </div>
                      ) : null}
                    </>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      <Offcanvas isOpen={selectedTenant !== null} toggle={closeEnrichment} direction="end">
        <OffcanvasHeader toggle={closeEnrichment}>
          {t("admin.cs.enrichment.drawerTitle")}
        </OffcanvasHeader>
        <OffcanvasBody>
          <EnrichmentCard
            enrichment={enrichment}
            loading={enrichmentLoading}
            tenantName={selectedTenant?.name}
          />
        </OffcanvasBody>
      </Offcanvas>
    </React.Fragment>
  );
};

export default SuperAdminTrialQueue;
